import { useEffect, useState } from 'react'

import { ListDescription, Separator } from 'components'
import BaseBridge, { IWbUserLocation } from 'config/bridge/BaseBridge'
import { ActionIconEnum } from 'enums/ActionIcon'
import { PageTitles } from 'enums/PageTitles'
import { PetTypeEnum } from 'enums/PetType'
import { TagEventsEnum } from 'enums/TagEvents'
import { useLocalErrorHandler } from 'hooks/useLocalErrorHandler'
import { useNavBar } from 'hooks/useNavBar'
import { ShimmerHome } from 'pages/home/Home/ShimmerHome/ShimmerHome'
import { useHistory } from 'react-router-dom'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { IQuickAccessBar } from 'types/IAfterSales'
import { Icons } from 'utils/icons'
import BridgeService from 'services/bridgeService'
import slider01 from 'assets/img/onboarding01.png'
import slider02 from 'assets/img/onboarding02.png'
import slider03 from 'assets/img/onboarding03.png'
import { preloadImages } from 'utils/image'

import { uuid } from '@interco/inter-ui'
import { Babi } from '@interco/inter-ui/components/Babi'
import { Button } from '@interco/inter-ui/components/Button'
import { Flex } from '@interco/inter-ui/components/Flex'
import { Text } from '@interco/inter-ui/components/Text'

import { Accordions } from './Accordions/Accordions'
import * as S from './AfterSales.styles'

import { AfterSalesStatusTag } from '..'

import { AnalyticsAfterSales } from './AfterSales.tags'

export const AfterSales = () => {
  const history = useHistory()
  const handlerError = useLocalErrorHandler()

  const isLoading = useBoundState((state) => state.isLoading)
  const afterSales = useBoundState((state) => state.afterSales)
  const getLandingPage = useBoundState((state) => state.getLandingPage)
  const setPet = useBoundState((state) => state.setAfterSalesPet)
  const getCoveredClinicsAfterSales = useBoundState((state) => state.getCoveredClinicsAfterSales)

  useNavBar({
    navbarTitle: PageTitles.PET,
    actionIcons: {
      leftIcon: ActionIconEnum.BACK,
    },
  })

  const [userLocation, setUserLocation] = useState<IWbUserLocation | null>(null)

  useEffect(() => {
    AnalyticsAfterSales(TagEventsEnum.PET_FLOW)
  }, [])

  useEffect(() => {
    preloadImages([slider01, slider02, slider03])
  }, [])

  useEffect(() => {
    BaseBridge.getUserLocation().then((location) => {
      setUserLocation(location)
    })
  }, [])

  const petIcons = {
    dog: <Icons.Dog width={24} height={24} color="var(--primary500)" />,
    cat: <Icons.Cat width={24} height={24} color="var(--primary500)" />,
    chevronRight: <Icons.ChevronRight width={24} height={24} color="var(--primary500)" />,
  }

  const quickAccessIcons = {
    add: <Icons.Add width={24} height={24} color="var(--primary500)" />,
    location: <Icons.Location width={24} height={24} color="var(--primary500)" />,
    pdf: <Icons.Pdf width={24} height={24} color="var(--primary500)" />,
    handUser: <Icons.HandUser width={24} height={24} color="var(--primary500)" />,
    infoCircle: <Icons.InfoCircle width={24} height={24} color="var(--primary500)" />,
  }

  const fetchCoveredClinicsResponse = () => {
    getCoveredClinicsAfterSales({
      history,
      pathname: TypesRoutes.PARTNER_CLINICS,
      latitude: userLocation?.latitude,
      longitude: userLocation?.longitude,
    })
  }

  const openProductManual = async () => {
    try {
      await BaseBridge.openPdf(afterSales.productManual, 'Manual do produto')
    } catch (error) {
      handlerError(error as Error, 'AfterSales.openProductManual')
    }
  }

  const openWaitingPeriodPdf = () => {
    const openPdfPromise = BaseBridge.openPdf(
      afterSales.urlPdfGracePeriods,
      'Coparticipação e Carência',
    )
    openPdfPromise.catch((error) => {
      handlerError(error as Error, 'AfterSales.openWaitingPeriodPdf')
    })
  }

  const openHowUsePlan = () => {
    history.push(TypesRoutes.HOW_USE)
  }

  const quickAccessBar: IQuickAccessBar[] = [
    {
      icon: quickAccessIcons.add,
      description: 'Adicionar outro Pet',
      route: () => {
        getLandingPage({
          history,
          pathname: TypesRoutes.HOME,
        })
        BridgeService.ticWb({
          name: 'btn_add_another_pet',
          module: 'pet',
          action: 'open_home_add_another_page',
          params: {
            afterSales: JSON.stringify(afterSales),
            getLandingPage: JSON.stringify(getLandingPage),
          },
        })
      },
    },
    {
      icon: quickAccessIcons.handUser,
      description: 'Carência de serviços',
      route: () => {
        openWaitingPeriodPdf()
      },
    },
    {
      icon: quickAccessIcons.location,
      description: 'Rede credenciada',
      route: () => {
        fetchCoveredClinicsResponse()

        BridgeService.ticWb({
          name: 'btn_partner_clinics',
          module: 'pet',
          action: 'open_partner_clinics_via_after_sales',
          params: {
            afterSales: JSON.stringify(afterSales),
          },
        })
      },
    },
    {
      icon: quickAccessIcons.infoCircle,
      description: 'Como usar o plano',
      route: () => {
        BridgeService.ticWb({
          name: 'btn_how_use_plan',
          module: 'pet',
          action: 'open_how_use_plan',
          params: {
            afterSales: JSON.stringify(afterSales),
          },
        })

        openHowUsePlan()
      },
    },
    {
      icon: quickAccessIcons.pdf,
      description: 'Manual do produto',
      route: openProductManual,
    },
  ]

  const renderMyPets = afterSales.pets?.map((pet) => {
    const handlerClickPet = () => {
      setPet(pet)
      history.push(TypesRoutes.AFTER_SALES_DETAILS)

      BridgeService.ticWb({
        name: 'click_pet_after_sales',
        module: 'pet',
        action: 'open_pet_details',
        params: {
          setPet: JSON.stringify(setPet),
          afterSales: JSON.stringify(afterSales),
          getLandingPage: JSON.stringify(getLandingPage),
          getCoveredClinicsAfterSales: JSON.stringify(getCoveredClinicsAfterSales),
        },
      })
    }

    const tagCancelDate = pet.cancelDate
    return (
      <ListDescription
        key={pet.id}
        iconLeft={pet.type === PetTypeEnum.DOG ? petIcons.dog : petIcons.cat}
        iconRight={petIcons.chevronRight}
        withBorder
        margin="16px 0"
        onClick={handlerClickPet}
      >
        <Flex direction="column">
          <Text variant="body-2" color="var(--gray500)" bold>
            {pet.name}
          </Text>
          <AfterSalesStatusTag status={pet.status} cancelDate={tagCancelDate} />
        </Flex>
      </ListDescription>
    )
  })

  const renderQuickAccess = quickAccessBar.map((item) => (
    <Button key={uuid()} variant="link" onClick={item.route}>
      <S.QuickAccessButton>{item.icon}</S.QuickAccessButton>
      <Text variant="caption-2" bold colorWeight={500}>
        {item.description}
      </Text>
    </Button>
  ))

  return isLoading ? (
    <ShimmerHome />
  ) : (
    <S.Container>
      <S.MyPets>{renderMyPets}</S.MyPets>
      <S.QuickAccess>{renderQuickAccess}</S.QuickAccess>
      <Separator margin="32px 0" />
      <Accordions />
      <Separator margin="32px 0" />
      <S.Footer>
        <Babi origin={TagEventsEnum.PRODUCT} />
      </S.Footer>
    </S.Container>
  )
}
